/* eslint-disable import/prefer-default-export */
import useAxios, { useFileUpdateAxios } from '../hooks/useAxios';

/**
 * Get setting config types.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getSettingConfigTypes(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios('/configType', data, true, cancelToken);
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Get powder batch temperature.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getPowderBatchTemperatureList(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios(
    '/deviceTemperatureConfig/getDeviceId',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.({ powderBatch: result.resPaged.content, count: result.resPaged.totalElements });
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Add powder batch temperature.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function addPowderBatchTemperature(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios(
    '/deviceTemperatureConfig/batch',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Delete powder batch temperature.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {Object} params.data.powderBatch - The data object containing the following properties:
 * @param {Object} params.data.powderBatch.configId - The config ID.
 * @param {Object} params.data.powderBatch.isEnabled - 1 is enabled, 0 is disabled.
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function deletePowderBatchTemperature(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios(
    '/deviceTemperatureConfig/deleteByDeviceId',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Precheck powder batch temperature file.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function precheckPowderBatchTemperatureFile(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const { deviceId, configFile } = data;
  const formData = new FormData();
  formData.append('file', configFile);
  const [status, result, rtCode] = await useFileUpdateAxios(
    `/v2/deviceTemperatureConfig/${deviceId}/upload/preCheck`,
    formData,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.({
      precheckResult: result.resSizeAndTempByDeviceIdList.map((item) => ({
        diameter: item.size?.toString(),
        temperature: item.temperature?.toString(),
      })),
    });
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Get powder batch temperature config list.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getPowderBatchTemperatureConfigList(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios(
    '/deviceTemperatureConfig/getSizeAndTemperature',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.({
      configList: result?.resSizeAndTempByDeviceIdList?.map((item) => ({
        diameter: item.size?.toString(),
        temperature: item.temperature?.toString(),
      })),
    });
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Add powder batch temperature config.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function addPowderBatchTemperatureConfig(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios(
    '/deviceTemperatureConfig/addSizeAndTemperature',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Edit powder batch temperature config.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function editPowderBatchTemperatureConfig(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios(
    '/deviceTemperatureConfig/updateTemperature',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Delete powder batch temperature config.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function deletePowderBatchTemperatureConfig(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios(
    '/deviceTemperatureConfig/deleteSizeAndTemperature',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Get material list.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getMaterialList(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios('/material/list', data, true, cancelToken);
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Get material type list.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {Object} params.data.materialId - The material ID.
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getMaterialTypeList(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios(
    `/material/${data.materialId}/material-type/list?includeDisable=true`,
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Add material type.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {Object} params.data.materialId - The material ID.
 * @param {Object} params.data.materialTypeName - The material type name.
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function addMaterialType(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios(
    `/material/${data.materialId}/addType`,
    { materialTypeName: data.materialTypeName },
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Edit material type.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {Object} params.data.materialTypeId - The material type ID.
 * @param {Object} params.data.materialTypeName - The material type name.
 * @param {Object} params.data.isEnabled - true is enabled, false is disabled.
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function editMaterialType(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const { isEnabled, materialTypeId } = data;
  const [status, result, rtCode] = await useAxios(
    isEnabled
      ? `/material-type/${materialTypeId}/enabled`
      : `/material-type/${materialTypeId}/disabled`,
    {},
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Delete material type.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {Object} params.data.materialTypeId - The material type ID.
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function deleteMaterialType(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios(
    `/material-type/${data.materialTypeId}/delete`,
    {},
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Get controlled forming elno.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getControlledFormingElNoList(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios(
    '/forming-control-spec/list',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.({ elNoList: result.resPaged.content, count: result.resPaged.totalElements });
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Get elno list by product name.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function getElNoListByProductName(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios(
    '/forming-control-spec/product-materials',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.({
      elNoList: result.items.map((item) => ({
        productName: result.productName,
        elNo: item.elNo,
        materialName: item.material,
        controlled: item.controlled,
      })),
    });
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Add controlled forming elnos.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {Array} params.data.elNoList - The elno list to add, each item contains the following properties:
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function addControlledFormingElNo(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios(
    '/forming-control-spec/batch-create',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}

/**
 * Delete controlled forming elno.
 * @param {Object} params - The parameters object.
 * @param {Object} params.data - The data object containing the following properties:
 * @param {string} params.data.elno - The target elno to delete.
 * @param {CancelToken} params.cancelToken - The cancel token for request cancellation.
 * @param {function} [params.onSuccess] - The function to call when the request is successful.
 * @param {function} [params.onError] - The function to call when the request is failed.
 * @param {function} [params.onSettled] - The function to call when the request is finished.
 * @returns {Promise<any>} The API response.
 */
export async function deleteControlledFormingElNo(params) {
  const { data, cancelToken, onSuccess, onError, onSettled } = params;
  const [status, result, rtCode] = await useAxios(
    '/forming-control-spec/delete',
    data,
    true,
    cancelToken
  );
  onSettled?.();
  if (status) {
    onSuccess?.(result);
  } else {
    onError?.(result, rtCode);
  }
}
